import { styled } from '@mui/material/styles';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useContext, useRef } from 'react';

import {
  ColRowHighlightContext,
  RowOffsetContext,
  useSwimlaneRootProps,
  useSwimlaneSlot,
} from '@/components/ui/swimlane/context';
import {
  COL_ROW_HIGHLIGHT_COLOR,
  GRID_GAP,
  ROW_HEADER_WIDTH,
} from '@/components/ui/swimlane/layout/constants';
import { SwimlaneRowHeaderResize } from './SwimlaneRowHeaderResize';

interface IRowHeaders {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function RowHeaders({ rowVirtualizer }: IRowHeaders) {
  const { component: SlotRowHeader, props: rowHeaderSlotProps } = useSwimlaneSlot('rowHeader');
  const { rowHighlightIndex, setRowHighlightIndex } = useContext(ColRowHighlightContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    rowAxisLabelText,
    rowHeaders,
    rowHeaderOrientation,
    rowHeadersWidth,
    isHeadersSticky,
    backgroundColor,
  } = useSwimlaneRootProps();
  const hasAxisLabelRow = typeof rowAxisLabelText === 'string' && isHeadersSticky;
  const rowHeaderWidth =
    rowHeaderOrientation === 'vertical' ? ROW_HEADER_WIDTH.vertical.value : rowHeadersWidth;

  if (rowHeaders.length === 0) {
    return null;
  }

  // styles for highlighting row header
  const rowHighLightStyles = {
    backgroundColor: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    borderTopLeftRadius: '6px',
    borderBottomLeftRadius: '6px',
  };

  return (
    <Container
      isStickyHeaders={isHeadersSticky}
      backgroundColor={backgroundColor}
      ref={containerRef}
      style={{
        gridArea: 'rowH',
        width: `${rowHeaderWidth}px`,
        height: `${rowVirtualizer.getTotalSize()}px`,
        left: `${hasAxisLabelRow ? `${GRID_GAP.value + ROW_HEADER_WIDTH.vertical.value}px` : '0px'}`,
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const rowHeaderData = rowHeaders[virtualRow.index];
        const isRowHighlighted = rowHighlightIndex === virtualRow.index;
        return (
          <RowHeaderContainer
            key={virtualRow.key}
            style={{
              width: `${rowHeaderWidth}px`,
              minHeight: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start}px)`,
              ...(isRowHighlighted ? rowHighLightStyles : {}),
              backgroundColor,
            }}
            onMouseEnter={() => setRowHighlightIndex(virtualRow.index)}
            onMouseLeave={() => setRowHighlightIndex(-1)}
          >
            <RowOffsetContext.Provider
              value={{
                offset: virtualRow.start,
                height: virtualRow.size,
                container: rowVirtualizer.scrollElement || null,
              }}
            >
              {rowHeaderData && (
                <SlotRowHeader
                  {...rowHeaderSlotProps}
                  {...rowHeaderData}
                  id={String(rowHeaders[virtualRow.index]?.id)}
                />
              )}
            </RowOffsetContext.Provider>
          </RowHeaderContainer>
        );
      })}
      <SwimlaneRowHeaderResize
        rowHeaderWidth={rowHeaderWidth}
        offset={containerRef.current?.getBoundingClientRect().left || 0}
        rowHeadersContainer={containerRef.current}
        scrollContainer={rowVirtualizer.scrollElement}
      />
    </Container>
  );
}

const RowHeaderContainer = styled('div')({
  position: 'absolute',
});

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isStickyHeaders' && prop !== 'backgroundColor',
})<{ isStickyHeaders: boolean; backgroundColor?: string }>(
  ({ isStickyHeaders, backgroundColor }) => ({
    backgroundColor: backgroundColor || '#fff',
    position: isStickyHeaders ? 'sticky' : 'relative',
    zIndex: '10',
  }),
);
